import { type ImportedLocation } from 'api/location-importer.api';
import {
  type HaulRoute,
  type RouteDistancePathOption,
  type WidthSegment,
} from 'api/haul-route.api';
import {
  decodeGeohash,
  pathGeogToGeoPoints,
  zoneGeogToGeoPoints,
  type GeoPoint,
} from 'utils/geohash';
import {
  CENTROID_PIN_ICON_PX,
  GRADE_CATEGORY_COLORS,
  LOCATION_TYPE_COLORS,
} from './roadsImporter.constants';

export const zoneColorHex = (
  locationType: string | undefined,
  fallback: string
) => {
  const type = locationType?.toLowerCase();
  if (!type) return fallback;
  return LOCATION_TYPE_COLORS[type] ?? fallback;
};

export const gradeCategoryColorHex = (
  category: string | null | undefined,
  fallback: string
) => {
  const cat = category?.toLowerCase();
  if (!cat) return fallback;
  return GRADE_CATEGORY_COLORS[cat] ?? fallback;
};

export const locationRing = (loc: ImportedLocation): GeoPoint[] =>
  zoneGeogToGeoPoints(loc.zoneGeog);

// The point a location actually renders around — prefers the stored
// centroidLat/Lon over the center of its drawn zone. Every boundary save
// already re-derives and overwrites centroidLat/Lon to match the polygon's
// vertex average (see saveGeohashes), so for a location that has never had
// its centroid manually moved (see "Move Centroid") this is exactly the
// same value the hull average would produce — the stored field only ever
// diverges from it once a user deliberately repositions it, and that
// deliberate placement is what should win. Falls back to the hull average
// when centroid fields are missing entirely (e.g. a boundary-only location
// that predates this field), and to null when there's no polygon either.
// Used both for the label position and for hit-testing (e.g. "did the road
// reach this location") so what the user sees lines up with what gets hit.
export const locationCenterPoint = (loc: ImportedLocation): GeoPoint | null => {
  if (loc.centroidLat != null && loc.centroidLon != null) {
    return { lat: loc.centroidLat, lon: loc.centroidLon };
  }
  const hull = locationRing(loc);
  if (hull.length >= 3) {
    return {
      lat: hull.reduce((s, p) => s + p.lat, 0) / hull.length,
      lon: hull.reduce((s, p) => s + p.lon, 0) / hull.length,
    };
  }
  return null;
};

// A map-pin billboard image (matches MUI's LocationOnIcon glyph) for the
// draggable "Move Centroid" marker — same inline-SVG-data-URI technique
// used for the truck billboards in useTruckMarkers.tsx, so no new asset
// file is needed and the color can follow the theme.
export const locationPinIconDataUri = (colorHex: string): string => {
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="${CENTROID_PIN_ICON_PX}" height="${CENTROID_PIN_ICON_PX}">` +
    `<path fill="${colorHex}" stroke="white" stroke-width="0.75" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>` +
    '</svg>';
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

// Shoelace-formula polygon area in degree-space — not a real area (no
// meters conversion), but locations near each other share roughly the same
// degrees-per-meter scale, so it's a valid *relative* size comparison.
// Used to pick the most specific match when a click lands inside more than
// one location's boundary (e.g. a small location nested inside a larger
// one) — the smaller polygon is almost always the one the user meant.
export const ringAreaApprox = (ring: GeoPoint[]): number => {
  let area = 0;
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i];
    const b = ring[(i + 1) % ring.length];
    area += a.lon * b.lat - b.lon * a.lat;
  }
  return Math.abs(area / 2);
};

// Auto-drafted starting boundary for a new Intersection location — a small
// circle around the click point the user can drag/adjust instead of having
// to trace a polygon by hand for what's usually just a point on the map.
export const buildCircleGeoPoints = (
  center: GeoPoint,
  radiusMeters: number,
  segments = 6
): GeoPoint[] => {
  const pointCount = Math.min(Math.max(Math.round(segments), 3), 6);
  const mLat = 111320;
  const mLon = 111320 * Math.cos((center.lat * Math.PI) / 180);
  const points: GeoPoint[] = [];
  for (let i = 0; i < pointCount; i++) {
    const angle = (2 * Math.PI * i) / pointCount;
    points.push({
      lat: center.lat + (radiusMeters * Math.sin(angle)) / mLat,
      lon: center.lon + (radiusMeters * Math.cos(angle)) / mLon,
    });
  }
  return points;
};

// Split a road path into runs of consecutive segments sharing one width,
// so a mid-drawing width change only affects the road from that point on
export const groupSegmentsByWidth = (
  path: GeoPoint[],
  widths: number[],
  fallback: number
): Array<{ width: number; points: GeoPoint[] }> => {
  const groups: Array<{ width: number; points: GeoPoint[] }> = [];
  for (let i = 0; i < path.length - 1; i++) {
    const width = widths[i + 1] ?? widths[widths.length - 1] ?? fallback;
    const last = groups[groups.length - 1];
    if (last && last.width === width) {
      last.points.push(path[i + 1]);
    } else {
      groups.push({ width, points: [path[i], path[i + 1]] });
    }
  }
  return groups;
};

// Rebuild the drawn path + per-segment widths from a saved route's
// widthSegments. The geohash from/to endpoints are cell centers, not the
// exact drawn point, so this can wobble a straight road by up to a cell
// width — kept only as a fallback for routes saved before pathGeog existed.
export const pathFromWidthSegments = (
  segments: WidthSegment[]
): { path: GeoPoint[]; widths: number[] } | null => {
  if (segments.length === 0) return null;
  const path: GeoPoint[] = [decodeGeohash(segments[0].from)];
  const widths: number[] = [segments[0].width];
  segments.forEach((seg) => {
    path.push(decodeGeohash(seg.to));
    widths.push(seg.width);
  });
  return { path, widths };
};

// widths[] shaped to match groupSegmentsByWidth's convention (widths[0] is
// unused filler, widths[i+1] is the width of the segment ending at path[i+1])
export const widthsForExactPath = (
  pointCount: number,
  segments: WidthSegment[] | null | undefined,
  fallbackWidth = 50
): number[] => {
  if (segments?.length === pointCount - 1) {
    return [segments[0].width, ...segments.map((s) => s.width)];
  }
  return new Array(pointCount).fill(fallbackWidth);
};

// The exact drawn path (full precision, no geohash rounding) when the route
// has pathGeog, else the geohash-decoded widthSegments reconstruction above.
export const reconstructRoutePath = (
  route: HaulRoute
): { path: GeoPoint[]; widths: number[] } | null => {
  const exactPath = pathGeogToGeoPoints(route.pathGeog);
  if (exactPath.length >= 2) {
    return {
      path: exactPath,
      widths: widthsForExactPath(exactPath.length, route.widthSegments),
    };
  }
  return route.widthSegments?.length
    ? pathFromWidthSegments(route.widthSegments)
    : null;
};

// Concatenates the exact road geometry of every segment in a Find Route
// result, in order, so the whole multi-hop route can be drawn as one
// highlighted path. Prefers each segment's real road shape (reusing
// reconstructRoutePath against the already-loaded route list); falls back to
// a straight line between the segment's two locations when its routeId
// isn't found locally (shouldn't normally happen — same customer, same
// route table — but keeps the highlight from silently dropping a leg).
export const buildRouteOptionPath = (
  option: RouteDistancePathOption,
  routes: HaulRoute[],
  locations: ImportedLocation[]
): GeoPoint[] => {
  const locationById = new Map(locations.map((loc) => [loc.locationId, loc]));
  const routeById = new Map(routes.map((r) => [r.routeId, r]));
  const path: GeoPoint[] = [];
  const appendPoint = (point: GeoPoint) => {
    const last = path[path.length - 1];
    if (last && last.lat === point.lat && last.lon === point.lon) return;
    path.push(point);
  };
  option.segments.forEach((segment) => {
    const route = routeById.get(segment.routeId);
    const reconstructed = route ? reconstructRoutePath(route) : null;
    if (reconstructed && reconstructed.path.length >= 2) {
      reconstructed.path.forEach(appendPoint);
      return;
    }
    const fromPoint = locationById.get(segment.fromLocationId);
    const toPoint = locationById.get(segment.toLocationId);
    const from = fromPoint ? locationCenterPoint(fromPoint) : null;
    const to = toPoint ? locationCenterPoint(toPoint) : null;
    if (from) appendPoint(from);
    if (to) appendPoint(to);
  });
  return path;
};

// widths[i] (i>=1) is the width of the segment ending at path[i]; widths[0]
// is unused filler. Reversing the path means widths[1..] must reverse too,
// but stay offset by one so each width still lands on the right segment.
export const reverseRoadWidths = (widths: number[]): number[] => [
  widths[0] ?? 50,
  ...widths.slice(1).reverse(),
];

export const distToSegmentMeters = (
  p: GeoPoint,
  a: GeoPoint,
  b: GeoPoint
): number => {
  const mLat = 111320;
  const mLon = 111320 * Math.cos((p.lat * Math.PI) / 180);
  const px = (p.lon - a.lon) * mLon;
  const py = (p.lat - a.lat) * mLat;
  const bx = (b.lon - a.lon) * mLon;
  const by = (b.lat - a.lat) * mLat;
  const len2 = bx * bx + by * by;
  const along =
    len2 > 0 ? Math.max(0, Math.min(1, (px * bx + py * by) / len2)) : 0;
  return Math.hypot(px - along * bx, py - along * by);
};

export const haversineMeters = (a: GeoPoint, b: GeoPoint): number => {
  const R = 6371000;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLon = ((b.lon - a.lon) * Math.PI) / 180;
  const lat1 = (a.lat * Math.PI) / 180;
  const lat2 = (b.lat * Math.PI) / 180;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
};

export const pathDistanceMeters = (path: GeoPoint[]): number => {
  let total = 0;
  for (let i = 0; i < path.length - 1; i++) {
    total += haversineMeters(path[i], path[i + 1]);
  }
  return total;
};

// Two-way roads are saved as two independent HaulRoute records (one per
// direction, see queueRoad) with no field linking them — the reverse leg is
// found by matching from/to locations swapped.
export const findReverseRoute = (
  route: HaulRoute,
  routes: HaulRoute[]
): HaulRoute | null =>
  routes.find(
    (r) =>
      r.routeId !== route.routeId &&
      r.fromLocationId === route.toLocationId &&
      r.toLocationId === route.fromLocationId
  ) ?? null;

// Every other route that physically overlays `route` on the map: the
// reverse leg of a two-way road (see findReverseRoute above), plus any
// other route variant recorded between the same two locations in the same
// direction. Blocking/unblocking one of these without the others leaves
// the "other half" of what's really one physical road unaffected, so
// callers offer all of them together (see BlockRoadDialog/UnblockRoadDialog).
export const findOverlappingRoutes = (
  route: HaulRoute,
  routes: HaulRoute[]
): HaulRoute[] =>
  routes.filter(
    (r) =>
      r.routeId !== route.routeId &&
      ((r.fromLocationId === route.fromLocationId &&
        r.toLocationId === route.toLocationId) ||
        (r.fromLocationId === route.toLocationId &&
          r.toLocationId === route.fromLocationId))
  );

// <input type="number"> still lets the browser accept 'e', '+', '-', '.'
// keystrokes (scientific-notation syntax) — strip anything but digits so
// fields like capacity/lanes/speed limit can only ever hold whole numbers.
export const sanitizeIntegerInput = (raw: string): string =>
  raw.replace(/\D/g, '');

// Location names are used as route/geohash keys elsewhere (e.g. pending
// route keys are `${fromName},${toName}`), so spaces aren't allowed.
export const sanitizeNameInput = (raw: string): string =>
  raw.replace(/\s/g, '');

// Renders a min/max speed limit pair as a single label — a plain value when
// both ends match (the common case: one speed limit entered for the whole
// road), else a "min–max" range.
export const formatSpeedRange = (
  min: number | null | undefined,
  max: number | null | undefined
): string => {
  if (min == null && max == null) return '—';
  if (min == null) return `${max}`;
  if (max == null) return `${min}`;
  return min === max ? `${min}` : `${min}–${max}`;
};

// Route/road names come from the backend as `${fromName},${toName}` (see
// sanitizeNameInput above) — display that as a directional label instead of
// a raw comma-separated pair.
export const formatRoadName = (raw: string): string => {
  const parts = raw
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
  return parts.length > 1 ? parts.join(' -> ') : raw;
};

export const formatDistance = (meters: number): string =>
  meters >= 1000
    ? `${(meters / 1000).toFixed(2)} km`
    : `${Math.round(meters)} m`;

// Blocked-road TTL is reported in whole seconds remaining as of the last
// poll — round up to whole minutes for display so a road blocked 90s ago
// with a 2-minute TTL still reads as "1m left" rather than "0m left".
// `null` means blocked indefinitely (no TTL was set).
export const blockedTtlMinutesRemaining = (
  ttlSeconds: number | null
): number | null =>
  ttlSeconds == null ? null : Math.max(1, Math.ceil(ttlSeconds / 60));

// Combines the Block dialog's separate hours/minutes duration inputs into
// one total-minutes TTL. `undefined` means both were left blank (indefinite
// block — no TTL sent to the API). `null` means something was entered but
// it doesn't form a valid positive duration, so callers can disable Save.
export const parseBlockTtlMinutes = (
  hoursRaw: string,
  minutesRaw: string
): number | null | undefined => {
  const hoursTrimmed = hoursRaw.trim();
  const minutesTrimmed = minutesRaw.trim();
  if (!hoursTrimmed && !minutesTrimmed) return undefined;
  const hours = hoursTrimmed ? parseFloat(hoursTrimmed) : 0;
  const minutes = minutesTrimmed ? parseFloat(minutesTrimmed) : 0;
  if (!isFinite(hours) || !isFinite(minutes) || hours < 0 || minutes < 0) {
    return null;
  }
  const total = hours * 60 + minutes;
  return total > 0 ? total : null;
};

// Live "resend OTP"-style countdown clock: MM:SS under an hour, H:MM:SS
// once the remaining time crosses one. Callers pass a possibly-negative
// remaining ms (a tick can land just after the deadline); clamped to 0.
export const formatCountdownClock = (msRemaining: number): string => {
  const totalSeconds = Math.max(0, Math.ceil(msRemaining / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const mm = String(minutes).padStart(2, '0');
  const ss = String(seconds).padStart(2, '0');
  return hours > 0 ? `${hours}:${mm}:${ss}` : `${mm}:${ss}`;
};

// See ROAD_WIDTH_ZOOM_FACTOR — grows corridor width with camera height past
// the base width so roads don't shrink to sub-pixel and vanish when zoomed
// out.
export const zoomAwareRoadWidth = (
  cameraHeightMeters: number,
  baseWidthMeters: number,
  zoomFactor: number
): number => Math.max(baseWidthMeters, cameraHeightMeters * zoomFactor);
